import { ChangeDetectionStrategy, Component, Inject, Input, OnChanges, SimpleChanges } from '@angular/core';

import { BetType } from '@bpos/common';
import { OverAskConfig } from '@frontend/sports/common/client-config-data-access';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { SyncUserSettingsService, UserService } from '@frontend/sports/user/feature';
import { Store } from '@ngrx/store';
import { ArcRestrictionService } from 'packages/sports/web/app/src/arc/arc-restriction.service';
import { EpcotConfigService } from 'packages/sports/web/app/src/common/epcot-config.service';

import { IBetslipMessages } from '../../../../base/models/betslip-messages';
import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../base/services/betslip-messages-provider.service';
import { IBetslipRootState } from '../../../../base/store/state';
import { isSucceededNonPartialSlip } from '../../../betplacement/utils';
import { IBetslipResultSummaryViewModel } from '../../models/betslip-result-summary.model';
import { BetslipResultConfigurationBase } from '../../services/result-configuration.service';
import { createEnhancedSummary } from '../../utils/result-summary.utils';
import BaseResultComponent from './base-result.component';
import { IBetslipResultHeaderViewModel } from './result-header.component';

@Component({
    selector: 'bs-digital-result',
    templateUrl: './result.component.html',
    styleUrls: ['./result.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ResultComponent extends BaseResultComponent implements OnChanges {
    @Input() trackingInformation: string;
    @Input() showResultActions = true;
    @Input() isNavigationFromEditBet?: boolean;
    summaryViewModel: Nullable<IBetslipResultSummaryViewModel>;

    isSlipsDetailsOpen = false;
    messages: IBetslipMessages;

    constructor(
        private epcotConfig: EpcotConfigService,
        resultConfiguration: BetslipResultConfigurationBase,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messagesProvider: IBetslipMessagesProviderService,
        arcRestrictionService: ArcRestrictionService,
        store: Store<IBetslipRootState>,
        userService: UserService,
        overAskConfig: OverAskConfig,
        userSettings: SyncUserSettingsService,
    ) {
        super(arcRestrictionService, resultConfiguration, store, userService, overAskConfig, userSettings);

        this.messages = messagesProvider.getMessages();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['state'] || !this.state.summary) {
            return;
        }

        let isEachWay = false;

        //when we have single bet EW and combo EW we need to show total stake on the result screen
        if ((this.state.summary?.numberOfBets?.value ?? 0) === 1) {
            const succeededSlip = this.state.result.slips.find(isSucceededNonPartialSlip);

            if (succeededSlip) {
                isEachWay = succeededSlip.picks.some((pick) => (pick.participantBetDetails ?? pick.optionBetDetails)?.betType === BetType.EachWay);
            }
        }

        this.summaryViewModel = createEnhancedSummary(
            this.state.summary,
            isEachWay,
            {
                BoostedWinnings: this.messages.common.BoostedWinnings,
                numberOfBets: this.messages.result.numberOfBets,
                possibleWinnings: this.messages.result.possibleWinnings,
                possibleWinningsGross: this.messages.result.possibleWinningsGross,
                possibleWinningsNet: this.messages.result.possibleWinningsNet,
                stake: this.messages.result.stake,
                totalStake: this.messages.result.totalStake,
                totalOdds: this.messages.result.totalOdds,
                freebetText: this.messages.crmTokens.oddsBoost.betslipSummary.PayoutTypeFreebet,
                teaserStake: this.messages.result.teaserStake,
            },
            this.isTeaserBet,
        );
    }

    get resultHeaderViewModel(): IBetslipResultHeaderViewModel {
        return {
            resultMessage: this.state.result.resultMessage,
            goodLuckMessage: this.messages.result.goodLuck,
        };
    }

    get showQuickLinks(): boolean {
        return !this.inColumn && this.showQuickLinksForEditBetNavigation;
    }

    get showQuickLinksForEditBetNavigation(): boolean {
        return this.epcotConfig.isShowMyBetsPopup ? !this.isNavigationFromEditBet : true;
    }
}
